/*eslint-disable*/
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

// El mockup P01 (Academy Rediseño.dc.html) dibuja un navbar distinto al del
// embudo público: una barra navy 72px con logo blanco + tabs Aprender/Explorar/
// Logros + avatar. `AppTopNav` es ese header — vive fuera de header1/initialHeader
// para no tocar los tests de esos dos (siguen protegiendo el embudo público).
import Header from "../layout/header/AppTopNav";
import Footer from "../layout/footer/footer1";
import Testimonial2 from "../elements/testimonial2";
import ModalHome from "../components/ibos/ModalHome";
import ModalHomeProspect from "../components/prospect/ModalHomeProspect";
import HomeSkeleton from "../components/home/HomeSkeleton";
import HomeFeatured from "../components/home/HomeFeatured";
import HomeContinue from "../components/home/HomeContinue";
import HomeRecommended from "../components/home/HomeRecommended";
import HomeBusinessGrid from "../components/home/HomeBusinessGrid";
import LearningCard from "../components/learning/LearningCard";

import { useGetAll } from "../../hooks/course.hook";
import { useMyLearning } from "../../hooks/Course/detailCourse.hook";
import useCompletedCourses from "../../hooks/useCompletedCourses";
import useCertifiedCourses from "../../hooks/useCertifiedCourses";
import useIBO from "../../hooks/useIBO";
import useModal from "../../hooks/useModal";
import useProspect from "../../hooks/useProspect";
import { courseHelper } from "../../helpers";
import { SESSION_MOTIVO_KEY } from "../../api/API";
import { alertNotification } from "../../utils/alerts";
import { businessDotColor, courseBusinessName } from "../../utils/business";

// Textos de los códigos de sesión que `onSessionError` pasa como motivo
// one-shot en sessionStorage. La home lee la clave, muestra el toast y
// la limpia.
const MOTIVO_MESSAGES = {
  SESSION_REVOKED:
    "Tu sesión se cerró porque se cambió la contraseña. Inicia sesión de nuevo.",
  DEVICE_MISMATCH: "Tu sesión terminó. Inicia sesión de nuevo.",
  APPLICATION_MISMATCH: "Tu sesión terminó. Inicia sesión de nuevo.",
  DPOP_PROOF_INVALID: "Tu sesión terminó. Inicia sesión de nuevo.",
  TOKEN_INVALID: "Tu sesión terminó. Inicia sesión de nuevo.",
};
import { AuthContext } from "../../context/context";
import { IboContext } from "../../context/iboContext";
import { ProspectContext } from "../../context/prospectContext";
import { TrackingContext } from "../../context/trackingContext";
import Slider from "react-slick";
import { Icon } from "@iconify/react";
import chevronLeftIcon from "@iconify-icons/lucide/chevron-left";
import chevronRightIcon from "@iconify-icons/lucide/chevron-right";

/* Flechas custom para el carrusel de "En progreso". react-slick inyecta
   su propio markup gris por defecto; aquí usamos el design system (navy
   sobre blanco + sombra) y las posicionamos sobre las cards. Van en
   SVG para no cargar imágenes. Se ocultan cuando el carrusel está en el
   extremo (prop `className` incluye "slick-disabled"). */
function CarouselArrow({ className, style, onClick, direction }) {
  const disabled = className?.includes("slick-disabled");
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      aria-label={direction === "next" ? "Siguiente" : "Anterior"}
      aria-disabled={disabled}
      style={{
        ...style,
        position: "absolute",
        top: "50%",
        [direction === "next" ? "right" : "left"]: -8,
        transform: "translateY(-50%)",
        zIndex: 2,
        width: 40,
        height: 40,
        borderRadius: "50%",
        border: "1px solid var(--color-line)",
        background: disabled ? "var(--color-bg)" : "#fff",
        color: disabled ? "var(--color-ink-2)" : "var(--color-ink)",
        boxShadow: disabled ? "none" : "0 6px 18px rgba(16, 28, 54, 0.14)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "transform 150ms ease, box-shadow 150ms ease, opacity 150ms ease",
        padding: 0,
      }}
    >
      <Icon
        icon={direction === "next" ? chevronRightIcon : chevronLeftIcon}
        width={20}
        height={20}
        aria-hidden="true"
      />
    </button>
  );
}

/**
 * P01 · Aprender (Academy Rediseño.dc.html líneas 703-980).
 * La misma ruta `/` sirve cuatro escenarios según el estado del usuario:
 *   - nuevo/invitado (sin sesión): welcome + featured + explorar por negocio + testimonios
 *   - acceso (sesión sin cursos empezados): saludo + recomendado + explorar
 *   - recurrente (sesión + cursos en curso): saludo + continue + En progreso + explorar
 *   - logros (sesión + varios cursos completados): pausa (Q11-sexies — necesita
 *     endpoint de "cursos completados por usuario"); mientras tanto se trata como
 *     `recurrente` con un mensaje distinto.
 */
const Home = () => {
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const { iboCodeParam } = useParams();

  const { introductory, basicAndIntermediate, trainings } = useGetAll();
  const { userInfoQursiva } = useContext(AuthContext);
  // `iboInfo` viene del CONTEXT (no de localStorage.getItem en el render):
  // cuando el usuario introduce el código IBO en el ModalHome, el context
  // se actualiza y home re-renderiza en el siguiente tick. Leer del
  // localStorage funcionaba por casualidad de timing y es frágil.
  const { iboInfo, setPurchasedCourses } = useContext(IboContext);
  /* Defensive con `|| {}` para tests que no envuelven el
     ProspectProvider (courses.test.jsx). */
  const { prospectInfo } = useContext(ProspectContext) || {};
  const { topicActiv } = useContext(TrackingContext);

  // Handler ESTABLE: useMyLearning tiene `setShowModalError` en las deps de su
  // useEffect. Pasar una lambda inline crea función nueva en cada render →
  // dispara el useEffect → setData → re-render → nueva función → LOOP infinito
  // de llamadas a /course/mylearning. `useCallback` lo estabiliza.
  const noopModalError = useCallback(() => {}, []);
  const { data: myCourses } = useMyLearning(noopModalError);
  const { data: completedCourses } = useCompletedCourses();
  /* IDs de cursos con examen aprobado — para diferenciar "listo para
     examen" (todas las lecciones vistas, sin certificado) de "curso
     con certificado" en las cards de "En progreso". */
  const { data: certifiedCourses } = useCertifiedCourses();
  const certifiedIds = useMemo(
    () =>
      new Set(
        (Array.isArray(certifiedCourses) ? certifiedCourses : []).map(
          (c) => c.courseId
        )
      ),
    [certifiedCourses]
  );

  const { toggleModal } = useModal();
  const { validateIBO, iboCode, setIboCode } = useIBO(t, toggleModal);
  const [codeByUrl, setCodeByUrl] = useState(false);

  /* Gate del INVITADO reutilizado en el CTA "Ingresar" del saludo.
     Sin `contact` en localStorage, el botón abre el mismo
     `ModalHomeProspect` que se dispara desde /courses y /courses-details
     — así el visitante deja email/nombre desde el home antes de navegar
     al catálogo. Si YA tiene `contact`, el modal simplemente cierra al
     confirmar (el hook navega al detalle del último curso puesto, pero
     como no seteamos `codeCourse`, cae en el `else navigate(uri)` con
     uri undefined; el flujo desde el home no lo activa nunca en ese
     caso porque solo se pide para invitados sin datos). */
  const {
    handleSubmit: prospectSubmit,
    isVisibleProspect,
    toggleModalProspect,
    checkEmailInBD: prospectCheckEmail,
  } = useProspect(t);

  /* Cuatro estados de sesión:
     - `isLoggedIn` — sesión REAL con token (`userInfoQursiva`).
     - `isProspect` — visitante que dejó email+nombre en el gate
       (`contact` en localStorage vía `ProspectContext`). Se saluda por
       su nombre pero no tiene "sus cursos": el userRelationId del
       prospecto no está enlazado a compras propias todavía.
     - `isGuest` — visitante con código IBO pero SIN datos del
       prospecto todavía. Debe rellenar el gate primero.
     - resto — anónimo/nuevo. */
  const isLoggedIn = Boolean(userInfoQursiva);
  const isProspect = !isLoggedIn && Boolean(prospectInfo);
  const isGuest = !isLoggedIn && !isProspect && Boolean(iboInfo);
  const hasSession = isLoggedIn;
  const inProgressCount =
    isLoggedIn && Array.isArray(myCourses) ? myCourses.length : 0;
  const completedCount =
    isLoggedIn && Array.isArray(completedCourses)
      ? completedCourses.length
      : 0;

  const userState = useMemo(() => {
    if (isProspect) return "prospecto";
    if (isGuest) return "invitado";
    if (!hasSession) return "nuevo";
    // "Logros" a partir de 2 cursos completados — Q11-sexies decidido. El
    // mockup habla de "Has completado dos cursos" en el saludo del estado
    // 'logros', así que 2 es el umbral natural.
    if (completedCount >= 2) return "logros";
    if (inProgressCount === 0) return "acceso";
    return "recurrente";
  }, [hasSession, isProspect, isGuest, inProgressCount, completedCount]);

  // 1) IBO por URL: solo al montar (o si cambia el param, cosa rara).
  useEffect(() => {
    if (iboCodeParam !== undefined) {
      setIboCode({ iboCode: iboCodeParam });
      setCodeByUrl(true);
    }
    // eslint-disable-next-line
  }, [iboCodeParam]);

  // Motivo de cierre forzado por `onSessionError` (axios-dpop). El redirect
  // trae al usuario a `/` para que el ModalHome vuelva a aparecer; acá
  // leemos el motivo (one-shot en sessionStorage), mostramos un toast con
  // el mensaje descriptivo y limpiamos la clave para que no se repita.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let motivo = null;
    try {
      motivo = window.sessionStorage.getItem(SESSION_MOTIVO_KEY);
      if (motivo) window.sessionStorage.removeItem(SESSION_MOTIVO_KEY);
    } catch {
      /* storage bloqueado */
    }
    if (!motivo) return;
    const message = MOTIVO_MESSAGES[motivo];
    if (message) alertNotification(message);
  }, []);

  // 2) validateAccess CADA VEZ que cambia la sesión — el user puede iniciar
  // sesión dentro del ModalHome sin salir de esta ruta; antes el effect solo
  // corría al mount y `purchasedCourses` se quedaba vacío hasta recargar.
  useEffect(() => {
    if (!userInfoQursiva?.userRelationId) return;
    let cancelled = false;
    (async () => {
      const result = await courseHelper.validateAccess(
        userInfoQursiva.userRelationId
      );
      if (!cancelled) setPurchasedCourses(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [userInfoQursiva?.userRelationId, setPurchasedCourses]);

  useEffect(() => {
    async function validate() {
      if (iboCode.iboCode) {
        await validateIBO(true);
      }
    }
    validate();
    // eslint-disable-next-line
  }, [iboCode]);

  if (
    introductory.length === 0 ||
    basicAndIntermediate.length === 0 ||
    trainings.length === 0
  )
    return <HomeSkeleton t={t} />;

  const firstIntro = introductory[0];
  const recommended = basicAndIntermediate[0];
  const continueCourse = myCourses?.[0];

  /* Saludo — logueado usa su nombre; prospecto también (ya nos dejó
     sus datos en el gate); invitado no tiene nombre propio. */
  const greeting =
    userInfoQursiva?.firstName
      ? `Hola, ${userInfoQursiva.firstName}`
      : prospectInfo?.firstName
      ? `Hola, ${prospectInfo.firstName}`
      : "";

  const referrerName =
    iboInfo?.firstName || iboInfo?.name || iboInfo?.lastName || "";

  const homeTitle =
    userState === "acceso"
      ? "Empieza por aquí"
      : userState === "recurrente"
      ? "Continúa tu recorrido"
      : userState === "logros"
      ? "¡Vas muy bien!"
      : userState === "invitado"
      ? "Explora Academy como invitado"
      : userState === "prospecto"
      ? "Explora Academy"
      : null;

  const homeLead =
    userState === "acceso"
      ? "Tu código te da acceso a estos cursos. Aún no has empezado ninguno."
      : userState === "recurrente"
      ? `${inProgressCount} ${
          inProgressCount === 1 ? "curso" : "cursos"
        } en tu recorrido.`
      : userState === "logros"
      ? `Ya llevas ${completedCount} cursos completos. Este es un buen siguiente paso.`
      : userState === "invitado"
      ? referrerName
        ? `Estás como invitado, referido por ${referrerName}. Puedes ver los cursos libres; inicia sesión para llevar tu progreso.`
        : "Estás como invitado. Puedes ver los cursos libres; inicia sesión para llevar tu progreso."
      : userState === "prospecto"
      ? referrerName
        ? `Ya te tenemos registrado, referido por ${referrerName}. Descubre los cursos que puedes tomar.`
        : "Ya te tenemos registrado. Descubre los cursos que puedes tomar."
      : null;

  /* En "nuevo" (sin sesión ni IBO ni prospecto) el ModalHome montado
     abajo bloquea la pantalla — no se puede llegar a clickear una
     card. Aun así, gateamos aquí explícitamente: si el modal se
     cerrase por cualquier motivo (regresión, flujo raro), la card no
     debe navegar a un detalle que el usuario no puede consumir.
     Devolver sin navegar deja que el ModalHome siga cumpliendo su
     rol de "puerta única" del acceso. */
  const requiresAuthGate = userState === "nuevo";
  const openCourse = (courseCode) => {
    if (requiresAuthGate) return;
    if (courseCode) navigate(`/courses-details/${courseCode}`);
  };
  const openCatalog = () => {
    if (requiresAuthGate) return;
    navigate("/courses");
  };

  return (
    <>
      <Header />
      <main className="page-content bg-white home-page">
        {/* Layout de secciones — antes cada bloque manejaba su propio
            margin-bottom (26, 30, 8, nada, 12, …) y quedaba desigual.
            Ahora el padre es flex-column con gap uniforme, y cada
            sección es un <section> hijo directo sin margin propio.
            48 px separa bien las agrupaciones grandes en desktop; el
            ojo lee claramente dónde termina una y empieza otra. */}
        {/* home-sections — las sections hijas directas se separan con un
            divider sutil y más aire vertical (ver <style> abajo). El
            `gap` del flex se mantiene, pero el padding-bottom de cada
            section crea el espacio real donde pintar el borde. */}
        <style>{`
          .home-sections { display: flex; flex-direction: column; gap: 0; }
          .home-sections > section {
            padding-bottom: 56px;
            border-bottom: 1px solid var(--color-line);
          }
          .home-sections > section + section { padding-top: 56px; }
          .home-sections > section:last-child {
            border-bottom: 0;
            padding-bottom: 0;
          }
          .home-sections > section:first-child { padding-top: 0; }
          .home-section-heading {
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 0 0 18px;
          }
          .home-section-heading::before {
            content: "";
            display: inline-block;
            width: 4px;
            height: 22px;
            border-radius: 4px;
            background: var(--color-accent);
            flex: 0 0 auto;
          }
          .home-section-heading h2 {
            font-size: 20px;
            font-weight: 800;
            margin: 0;
            letter-spacing: -0.01em;
          }
          .home-section-heading .count {
            font-size: 13px;
            font-weight: 700;
            color: var(--color-ink-2);
            margin-left: auto;
          }
        `}</style>
        <div
          className="mx-auto home-sections"
          style={{
            maxWidth: 1160,
            padding: "20px 16px 120px",
          }}
        >
          <section>
            {userState === "nuevo" ? (
              <>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    letterSpacing: ".06em",
                    textTransform: "uppercase",
                    color: "var(--color-secondary-strong)",
                    marginBottom: 7,
                  }}
                >
                  Bienvenido a Academy
                </div>
                <h1
                  style={{
                    fontSize: 31,
                    lineHeight: 1.12,
                    fontWeight: 800,
                    margin: 0,
                    letterSpacing: "-.025em",
                    maxWidth: "22ch",
                  }}
                >
                  Invierte en ti, el resto llega
                </h1>
              </>
            ) : (
              <>
                {greeting && (
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#4A5568",
                      marginBottom: 2,
                    }}
                  >
                    {greeting}
                  </div>
                )}
                {homeTitle && (
                  <h1
                    style={{
                      fontSize: 27,
                      lineHeight: 1.2,
                      fontWeight: 800,
                      margin: "0 0 12px",
                      letterSpacing: "-.02em",
                      maxWidth: "20ch",
                    }}
                  >
                    {homeTitle}
                  </h1>
                )}
                {homeLead && (
                  <p
                    className="text-ink-2"
                    style={{
                      fontSize: 16,
                      lineHeight: 1.6,
                      fontWeight: 600,
                      margin: "0 0 16px",
                      maxWidth: "62ch",
                    }}
                  >
                    {homeLead}
                  </p>
                )}
                {userState === "invitado" && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      /* "Ingresar" abre el modal del gate del prospecto:
                         email + nombre + apellido. Al confirmar, el hook
                         guarda `contact` y el resto de superficies dejan
                         de pedir el gate. Antes decía "Iniciar sesión" y
                         llevaba a /login — pero un invitado con código
                         IBO no necesita login, necesita registrarse
                         como prospecto para que su IBO le dé
                         seguimiento. */
                      onClick={toggleModalProspect}
                      style={{
                        minHeight: 48,
                        padding: "0 20px",
                        borderRadius: 12,
                        border: 0,
                        background: "var(--color-accent)",
                        color: "var(--color-ink)",
                        fontSize: 15,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Ingresar
                    </button>
                    <button
                      type="button"
                      onClick={openCatalog}
                      style={{
                        minHeight: 48,
                        padding: "0 20px",
                        borderRadius: 12,
                        border: "1.5px solid var(--color-ink)",
                        background: "transparent",
                        color: "var(--color-ink)",
                        fontSize: 15,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Explorar catálogo
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          {userState === "nuevo" && firstIntro && (
            <section>
              <div className="home-section-heading">
                <h2>Empieza por lo esencial</h2>
              </div>
              <HomeFeatured
                title={firstIntro.name}
                meta={firstIntro.categoryCourse?.category?.name}
                thumbnail={firstIntro.thumbnail}
                onOpen={() => openCourse(firstIntro.code)}
              />
            </section>
          )}

          {userState === "recurrente" && continueCourse && (
            <section>
              <HomeContinue
                courseName={continueCourse.categoryCourse?.category?.name}
                lessonName={topicActiv?.subtopicName || continueCourse.name}
                // Q11-ter cableado: `progressPct` viene del backend
                // (`completedLessons / totalLessons`). Si el usuario aún no
                // ha empezado, es 0.
                progressPct={continueCourse.progressPct ?? 0}
                remainLabel={
                  continueCourse.totalLessons > continueCourse.completedLessons
                    ? `${continueCourse.totalLessons - continueCourse.completedLessons} de ${continueCourse.totalLessons} lecciones`
                    : null
                }
                resumeAt="Retoma aquí"
                onOpen={() => openCourse(continueCourse.code)}
                thumbnail={continueCourse.thumbnail}
              />
            </section>
          )}

          {(userState === "acceso" || userState === "logros") && recommended && (
            <section>
              <div className="home-section-heading">
                <h2>
                  {userState === "logros" ? "Tu siguiente paso" : "Curso recomendado"}
                </h2>
              </div>
              <HomeRecommended
                title={recommended.name}
                level={recommended.categoryCourse?.category?.name}
                why="Elegido para tu ruta actual — puedes verlo entero en menos de 2 horas."
                thumbnail={recommended.thumbnail}
                onStart={() => openCourse(recommended.code)}
                onSyllabus={() => openCourse(recommended.code)}
              />
            </section>
          )}

          {/* "En progreso" — recurrente siempre, y logros CUANDO tiene
              cursos activos además de los completados. Un usuario avanzado
              (>=2 completados) puede seguir teniendo cursos en marcha; no
              tiene sentido esconderlos. */}
          {(userState === "recurrente" ||
            (userState === "logros" && inProgressCount > 0)) &&
            myCourses.length > 0 && (
              <section>
                <div className="home-section-heading">
                  <h2>En progreso</h2>
                  <span className="count">
                    {inProgressCount}{" "}
                    {inProgressCount === 1 ? "curso" : "cursos"}
                  </span>
                </div>
                {/* Carrusel horizontal — 4 cards por fila en desktop,
                    responsive abajo. Si hay <=4 cursos no se ven las
                    flechas (`slidesToShow` iguala al total y queda sin
                    scroll). react-slick deshabilita las flechas en los
                    extremos añadiendo `slick-disabled` al className —
                    nuestro CarouselArrow lo detecta y las atenúa.
                    `padding: '0 10px'` en slick-slide separa las cards
                    (equivalente al gap de la grid anterior). */}
                <style>{`
                  .in-progress-carousel .slick-list { margin: 0 -8px; }
                  .in-progress-carousel .slick-track { display: flex !important; }
                  .in-progress-carousel .slick-slide { height: inherit !important; display: flex !important; }
                  .in-progress-carousel .slick-slide > div { padding: 0 8px; width: 100%; display: flex; }
                  .in-progress-carousel .slick-slide > div > * { flex: 1; }
                `}</style>
                <div style={{ position: "relative" }}>
                <Slider
                  className="in-progress-carousel"
                  dots={false}
                  infinite={false}
                  speed={400}
                  slidesToShow={Math.min(4, myCourses.length)}
                  slidesToScroll={1}
                  arrows={myCourses.length > 4}
                  nextArrow={<CarouselArrow direction="next" />}
                  prevArrow={<CarouselArrow direction="prev" />}
                  responsive={[
                    { breakpoint: 1024, settings: { slidesToShow: Math.min(3, myCourses.length) } },
                    { breakpoint: 768, settings: { slidesToShow: Math.min(2, myCourses.length) } },
                    { breakpoint: 480, settings: { slidesToShow: 1 } },
                  ]}
                >
                  {myCourses.map((item) => {
                    const business = courseBusinessName(item);
                    /* Estado del curso — cuatro variantes:
                       · sin empezar (progressPct 0)  → "Comenzar curso"
                       · en progreso                   → "Continuar"
                       · lecciones done, sin examen    → "Realizar examen"
                       · examen aprobado (certificado) → chip + "Repasar"
                       `item.completed` (Q11-ter) = todas las lecciones
                       vistas. El certificado se comprueba cruzando con
                       `certifiedIds` (endpoint getCertifiedCoursesByUser
                       — examen aprobado). Los dos no son lo mismo. */
                    /* Doble validación: además del `completed` del
                       backend, exigimos `completedLessons > 0`. Un bug
                       previo del backend marcaba como completado los
                       cursos con `totalLessons = 1` aunque estuvieran
                       en 0 lecciones vistas (regla `>= max(0, 0)` se
                       cumple con cero). Con el fix del backend esto
                       ya no debería aplicar, pero el guard evita
                       regresiones. */
                    const lessonsDone =
                      !!item.completed && (item.completedLessons ?? 0) > 0;
                    const examPassed = certifiedIds.has(item.id);
                    /* `hasExam` llega del backend (toCourseLearning). Si
                       el curso no tiene Evaluacion activa, el CTA
                       "Realizar examen" nunca aparece — se trata como
                       completado al terminar las lecciones. */
                    const hasExam = Boolean(item.hasExam);
                    const readyForExam = lessonsDone && !examPassed && hasExam;
                    const completedNoExam = lessonsDone && !hasExam;
                    const completed = examPassed || completedNoExam;
                    const progressPct = item.progressPct ?? 0;
                    const metaLine =
                      item.totalLessons > 0
                        ? `${item.completedLessons} de ${item.totalLessons} lecciones`
                        : null;
                    const onOpen = () => {
                      if (readyForExam)
                        return navigate(`/course-exam-info/${item.code}`);
                      return openCourse(item.code);
                    };
                    return (
                      <LearningCard
                        key={item.id}
                        title={item.name}
                        thumbnail={item.thumbnail}
                        /* Chip = LÍNEA DE NEGOCIO (Vision Travel,
                           Safeguard, …). Sin fallback a category.name
                           (que era el NIVEL del curso). */
                        businessName={business}
                        businessAccent={businessDotColor(business)}
                        /* Chip verde = examen aprobado O curso sin
                           examen con todas las lecciones vistas. */
                        completed={completed}
                        progressPct={progressPct}
                        metaLine={metaLine}
                        reviewLabel={completed ? "Repasar" : null}
                        openLabel={readyForExam ? "Realizar examen" : "Continuar"}
                        onOpen={onOpen}
                      />
                    );
                  })}
                </Slider>
                </div>
              </section>
            )}

          {/* "Explora por línea de negocio" — para los estados con
              sesión, el invitado y el prospecto. */}
          {(userState === "acceso" ||
            userState === "recurrente" ||
            userState === "logros" ||
            userState === "invitado" ||
            userState === "prospecto") && (
            <section>
              <div className="home-section-heading">
                <h2>Explora por línea de negocio</h2>
              </div>
              <HomeBusinessGrid onSelect={openCatalog} />
            </section>
          )}

          {userState === "nuevo" && introductory.length > 1 && (
            <section>
              <div className="home-section-heading">
                <h2>Cursos que puedes explorar</h2>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(272px, 1fr))",
                  gap: 14,
                }}
              >
                {introductory.slice(0, 6).map((c) => (
                  <LearningCard
                    key={c.id}
                    title={c.name}
                    thumbnail={c.thumbnail}
                    businessName={c.categoryCourse?.category?.name}
                    onOpen={() => openCourse(c.code)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Testimonios — para el visitante nuevo (evalúa), el
              invitado con código IBO (mismo caso: sigue evaluando si
              se afilia) y el usuario avanzado (le da pertenencia).
              `acceso` y `recurrente` se los ahorran para no cargar la
              home con contenido secundario cuando la acción principal
              ya está clara. */}
          {(userState === "nuevo" ||
            userState === "invitado" ||
            userState === "prospecto" ||
            userState === "logros") && <Testimonial2 t={t} />}
        </div>
      </main>

      <Footer t={t} />

      {!codeByUrl &&
        !localStorage.getItem("iboInfo") &&
        !localStorage.getItem("userInfoQursiva") && <ModalHome />}

      <ModalHomeProspect
        isVisibleProspect={isVisibleProspect}
        toggleModalProspect={toggleModalProspect}
        handleSubmit={prospectSubmit}
        checkEmailInBD={prospectCheckEmail}
      />
    </>
  );
};

export default Home;
